/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef } from '@angular/core';
import { FipModel, IpGroupModel, TemplateModel } from '../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService } from '../../services';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../enums';
import { takeUntil } from 'rxjs/operators';

export class ServiceBaseComponent extends BaseComponent {
    /**
     * External: Incoming template from parent component
     */
    public template: any;

    /**
     * External: Incoming template index from parent component
     */
    public templateIndex: number = 0;

    /**
     * The main formGroup variable
     */
    public serviceForm: FormGroup;

    /**
     * The current sub step name set from the subscriber.
     */
    public currentSubStepName: string;

    /**
     * Used in some templates
     */
    public namePrefix: string;

    /**
     * Stored advanced mode state.
     */
    public advancedMode: string = 'default';

    /**
     * Inputs grouped by step title
     */
    public subStepInputsByTitle: any = {};

    /**
     * Form initial hidden state
     */
    public inputStateStore: any = {};

    /**
     * Local reference to ENUM.
     */
    public subStepEnum = WizardSubStepTitlesEnum;

    /**
     * HTTP mode
     */
    public httpMode: boolean = false;

    /**
     * Component title
     * @private
     */
    private _componentTitle: WizardTitleEnum;

    protected constructor(
        protected cdr: ChangeDetectorRef,
        protected formBuilder: FormBuilder,
        protected languageService: LanguageService,
        protected lbErrorService: LbErrorService,
        protected logService: LogService,
        protected modalService: ModalService,
        protected route: ActivatedRoute,
        protected utilsService: UtilsService,
        protected wizardService: WizardService,
        protected wizardTemplateService: WizardTemplateService
    ) {
        super();
    }

    set componentTitle(title: WizardTitleEnum) {
        this._componentTitle = title;
    }

    get componentTitle() {
        return this._componentTitle;
    }

    /**
     * Grab the header model coming out of the headers component.
     * @param $event
     */
    public headerIds($event: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.HEADERS + '.headerIds').setValue($event);
    }

    /**
     * Mark fields with values as touched.
     * @param templatePatch
     * @param controlSet
     * @private
     */
    private static markFieldsWithValuesAsTouched(templatePatch: any, controlSet: AbstractControl<any, any>): void {
        for (const subControl in controlSet['controls']) {
            if (controlSet['controls'].hasOwnProperty(subControl)) {
                const inputField: any = controlSet['controls'][subControl];
                // If the input isn't disabled and the template has a value.
                if (!inputField.disabled && (templatePatch[subControl] && templatePatch[subControl].length)) {
                    inputField.markAsTouched();
                    inputField.markAsDirty();
                    inputField.updateValueAndValidity();
                }
            }
        }
    }

    /**
     * Get wizard sub step state
     * @param subStepName
     */
    public getWizardSubStepState(subStepName: string): boolean {
        return this.currentSubStepName !== subStepName;
    }

    /**
     * Loops through form inputs to show/hide and enable/disable them.
     * @param formController
     * @param formState
     * @param inputs
     * @param state
     * @param emitEvent
     * @private
     */
    public updateFormInputState(
        formController: any,
        formState: any,
        inputs: string[],
        state: boolean = false,
        emitEvent: boolean = true
    ): void {
        for (const input of inputs) {
            formState[input] = { hidden: state };
            if (state) {
                formController.get(input).disable({ emitEvent: emitEvent });
            } else {
                formController.get(input).enable({ emitEvent: emitEvent });
            }
        }
    }

    /**
     * Tells the view if an input should be shown or not.
     * @param controlName
     * @param options
     */
    public isInputHidden(controlName: string | number, options?: { force: any; }): boolean {
        if (options?.force) {
            return this.advancedMode === 'default';
        }

        if (this.inputStateStore[controlName]) {
            return this.inputStateStore[controlName].hidden;
        } else {
            return true;
        }
    }

    /**
     * Format IP groups
     * @param ipGroups
     */
    public formatIpGroups(ipGroups: IpGroupModel[]): IpGroupModel[] {
        return ipGroups?.length ? ipGroups : [];
    }

    /**
     * Format floating IPs
     * @param floatingIPs
     */
    public formatFloatingIPs(floatingIPs: FipModel[]): FipModel[] {
        const hasFips: boolean = !!floatingIPs?.length;
        if (hasFips && floatingIPs.length === 1) {
            this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').setValue(floatingIPs[0]);
        }

        return hasFips ? floatingIPs : [];
    }

    /**
     * Get for this specific service.
     * @param titleEnum
     */
    public getErrorForService(titleEnum: WizardTitleEnum): any {
        const serviceError = this.utilsService.getObjectValue(this.wizardService.controllerState, `errorState.${titleEnum}`);

        const templateId = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${[titleEnum]}.${this.templateIndex}.id.value`
        );

        if (templateId && templateId === serviceError?.id) {
            this.templateIndex = serviceError.index;

            return serviceError;
        } else {
            return null;
        }
    }

    /**
     * Get template for patch.
     * @param titleEnum
     * @param serviceError
     * @param id
     */
    public getTemplate(titleEnum: WizardTitleEnum, serviceError: { index: number; error: any; }, id?: string): any {
        if (serviceError?.index > -1) {
            this.templateIndex = serviceError.index;
            const template = this.setupComponentValues(titleEnum);
            this.enableHiddenFields(template, serviceError.error);

            return template;
        } else {
            return this.setupComponentValues(titleEnum, id);
        }
    }

    /**
     * Sets up the template and template patch for the component.
     * @param enumTitle
     * @param id
     */
    public setupComponentValues(enumTitle: WizardTitleEnum, id?: string): any {
        const controllerTemplate: any = id
            ? this.findExistingItemInControllerState(enumTitle, id)
            : this.utilsService.getObjectValue(this.wizardService.controllerState, `${[enumTitle]}.${this.templateIndex}`);

        const template: any = controllerTemplate
            ? controllerTemplate
            : this.utilsService.getObjectValue(this.wizardService.selectedTemplate, `hiddenSections.${[enumTitle]}.${this.templateIndex}`);

        return this.utilsService.duplicateObject(controllerTemplate ? controllerTemplate : template);
    }

    /**
     * Find existing item in controller state.
     * @param enumTitle
     * @param id
     */
    public findExistingItemInControllerState(enumTitle: WizardTitleEnum, id?: string): any {
        const controllerServices: any[] = this.utilsService.getObjectValue(this.wizardService.controllerState, `${[enumTitle]}`);
        const index = controllerServices.findIndex(item => item.id.value === id);
        if (index !== -1) {
            return controllerServices[index];
        }
    }

    /**
     * Formats services.
     * Concatenates controller state services and API services.
     * @param apiServices
     * @param WizardTitleEnum
     */
    public formatServices(apiServices: any[], WizardTitleEnum: WizardTitleEnum): any[] {
        const controllerServices = this.getValuesFromControllerState(WizardTitleEnum);

        return apiServices?.length
            ? controllerServices.concat(apiServices)
            : controllerServices;
    }

    /**
     * Filter services by name
     * @param template
     * @param servicesList
     * @param property
     */
    public filterServicesByName(template: any, servicesList: any[], property = 'name'): any[] {
        const propertyToCheck: any = this.utilsService.getObjectValue(template, `${property}.value`);

        return propertyToCheck ? servicesList.filter(service => service[property] !== propertyToCheck) : servicesList;
    }

    /**
     * Loop through the template creating patch values for the form.
     * @param template
     */
    public createTemplatePatch(template: any): any {
        const clonedTemplate: any = this.utilsService.duplicateObject(template);
        const templatePatch = {};
        for (const property in clonedTemplate) {
            if (template.hasOwnProperty(property) && property !== 'headerIds') {
                // Remove placeholders if not attached to a prefix or suffix.
                templatePatch[property] = template[property]?.value === '[placeHolder]'
                    ? ''
                    : template[property].value;
            }
        }

        return templatePatch;
    }

    /**
     * Get global names from controller state.
     * @param WizardTitleEnum
     */
    public getValuesFromControllerState(WizardTitleEnum: string): any[] {
        const controllerState: any = this.wizardService.controllerState;
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        const convertedServices: any[] = [];
        if (services?.length) {
            for (const service of services) {
                const convertedService: any = this.wizardTemplateService.convertTemplateToService(service);
                convertedService.id = service.id?.value ? service.id.value : 'temp-' + Date.now() + Math.random();
                convertedServices.push(convertedService);
            }
        }

        return convertedServices;
    }

    /**
     * Register sub steps with the wizard service.
     * @param titleEnum
     */
    public registerSubSteps(titleEnum: WizardTitleEnum): void {
        // Create sub steps title array from ENUM.
        const subStepTitles: Array<string> = Object.values(WizardSubStepTitlesEnum);
        const serviceError = this.utilsService.getObjectValue(this.wizardService.controllerState, `errorState.${titleEnum}`);

        // Check if sub step already exist, if not register them.
        if (!this.wizardService.activeStepHasSubSteps() || serviceError) {
            this.wizardService.registerSubStepsWithState(
                subStepTitles, this.subStepInputsByTitle, this.inputStateStore
            );
        }
    }

    /**
     * If template name contains [placeHolder] remove it and create name prefix
     * If the template doesn't have a name property use the new value
     * @param templatePatch
     * @param newValue
     */
    public formatLabel(templatePatch: any, newValue: string): void {
        if (templatePatch['name'] && templatePatch['name'].indexOf('[placeHolder]') !== -1) {
            this.namePrefix = templatePatch['name'].replace('[placeHolder]', '');

            if (newValue?.includes(this.namePrefix)) {
                const replacementValue = newValue.replace(this.namePrefix, '');
                templatePatch['name'] = replacementValue === '[placeHolder]' ? '' : replacementValue;
            } else {
                templatePatch['name'] = '';
            }
        } else {
            templatePatch['name'] = newValue;
        }
    }

    /**
     * Patch Template hidden state to form hidden state.
     * @param template
     * @param inputStateStore
     */
    public patchTemplateHiddenState(template: TemplateModel, inputStateStore: any): void {
        for (const property in template) {
            if (template.hasOwnProperty(property) && property !== 'subSteps' && property !== 'title') {
                if (!inputStateStore[property]) {
                    inputStateStore[property] = {
                        hidden: true
                    };
                }

                inputStateStore[property] = {
                    hidden: template[property].hidden
                };
            }
        }
    }

    /**
     * Check name for duplicates in a list and validate.
     * @param name
     * @param list
     * @param property
     * @param navigate
     */
    public checkNameForDuplicates(name: string, list: any[], property = 'name', navigate: boolean = false): void {
        const names: string[] = list.map((item: any): any => item[property]);
        const cleanArray: string[] = this.utilsService.cleanArray(names);
        const match: any = cleanArray ? cleanArray.find((item: string): boolean => item.toLowerCase() === name.toLowerCase()) : [];

        if (match && cleanArray.length) {
            const extendedMessage: string = this.getExtendedMessage(name, names);
            const input: AbstractControl = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.' + property);
            if (!input.hasError('duplicateName')) {
                const timerId = setTimeout((): void => { // Another Angular formBuilder timing issue
                    input.setErrors({ 'duplicateName': `${this.languageService.translate('duplicate_name')}` + extendedMessage });
                    input.markAsTouched();
                    clearTimeout(timerId);
                }, 0);
            }

            if (navigate) {
                const activeStepIndex: number = this.wizardService.activeStepIndex;
                const subSteps: any = this.wizardService.wizardSteps[activeStepIndex].subSteps;
                const subStepValue: any = subSteps.length
                    ? subSteps.findIndex((item: {
                        name: WizardSubStepTitlesEnum;
                    }): boolean => item.name === WizardSubStepTitlesEnum.GENERAL)
                    : 0;
                this.wizardService.goToStep(activeStepIndex, subStepValue);

                this.cdr.detectChanges();
            }
        }
    }

    /**
     * Gets the first frontend name from the frontendLabelAndMode @Input.
     * @private
     */
    public getFrontendName(): string {
        return this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.FRONTENDS}.${this.templateIndex}.name.value`
        );
    }

    /**
     * Get extended message for duplication error.
     * @param name
     * @param list
     */
    public getExtendedMessage(name: string, list: any[]): string {
        let extendedMessage = '';
        if (list.length > 4) {
            const cleanedLabel = name.toLowerCase().replace(/[0-9]/g, '');
            const partialMatches = list.filter(item => item.toLowerCase().indexOf(cleanedLabel) !== -1);
            // If there are at least two partial matches show the user the last name that is similar.
            extendedMessage = partialMatches.length > 2
                ? 'The last similar name was: ' + partialMatches[partialMatches.length - 1]
                : 'The last name create was: ' + list[list.length - 1];
        }

        return extendedMessage;
    }

    /**
     * Sets up the subStepInputsByTitle and inputStateStore variables from the form structure.
     * @param enumTitle
     */
    public setupClassVariablesFromForm(enumTitle?: WizardTitleEnum): void {
        const controllerState = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${[enumTitle]}.${this.templateIndex}`
        );

        const controls = this.serviceForm.controls;
        for (const control in controls) {
            if (controls.hasOwnProperty(control)) {
                this.subStepInputsByTitle[control] = Object.keys(controls[control]['controls']);
                for (const input in controls[control]['controls']) {
                    if (controls[control]['controls'].hasOwnProperty(input)) {
                        this.inputStateStore[input] = controllerState && controllerState[input]
                            ? { hidden: controllerState[input].hidden }
                            : { hidden: true };
                    }
                }
            }
        }
    }

    /**
     * Remove null value properties.
     * If a property is set as null in the template remove the property so the default if used.
     * @param templatePatch
     */
    public removeNullValueProperties(templatePatch: any): void {
        for (const property in templatePatch) {
            if (templatePatch.hasOwnProperty(property)) {
                if (templatePatch[property] === null) {
                    delete templatePatch[property];
                }
            }
        }
    }

    /**
     * Apply the template patch to the form.
     * @param templatePatch
     */
    public applyPatchAndValidate(templatePatch: any): void {
        const controls = this.serviceForm.controls;

        for (const property in controls) {
            if (controls.hasOwnProperty(property)) {
                const controlSet = controls[property];
                controlSet.patchValue(templatePatch);

                ServiceBaseComponent.markFieldsWithValuesAsTouched(templatePatch, controlSet);
            }
        }
    }

    /**
     * Checks each visible step for validity.
     */
    public isCurrentStepValid(): boolean {
        const currentStep = this.serviceForm.get(this.currentSubStepName);
        const isValid: boolean = currentStep?.valid || false;
        this.wizardService.isSectionValidEmitter.next(isValid);

        return isValid;
    }

    /**
     * Subscribe to the wizard to get the section name to be viewed.
     */
    public setupBaseSubscriber(): void {
        this.wizardService.subStepNameEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(sectionName => {
                this.currentSubStepName = sectionName;
                this.isCurrentStepValid();
            });
    }

    /**
     * Convert service form value to service model ready for output
     * @param model
     */
    public flattenServiceFormValues(model: any): any {
        const outputObject = model;

        for (const control in this.serviceForm.controls) {
            if (this.serviceForm.controls.hasOwnProperty(control)) {
                const values = this.serviceForm.controls[control].value;
                Object.assign(outputObject, values);
            }
        }

        return outputObject;
    }

    /**
     * Find errored inputs and navigates to the first one.
     * @param errorArray
     */
    public loadedWithErrors(errorArray: any): void {
        // Create sub steps title array from ENUM.
        const subStepTitles: Array<string> = Object.values(WizardSubStepTitlesEnum);
        this.wizardService.isSectionValidEmitter.next(false);
        if (errorArray?.length) {
            const classVariables = {
                cdr: this.cdr,
                componentTitle: this._componentTitle,
                form: this.serviceForm,
                subStepTitles: subStepTitles,
                subStepInputsByTitle: this.subStepInputsByTitle,
                currentStepName: this.currentSubStepName,
                inputStateStore: this.inputStateStore
            };

            this.lbErrorService.setClassVariables(classVariables);
            this.lbErrorService.loadedWithErrors(errorArray);
        }
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.serviceForm.get(group) as FormGroup;
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Enable hidden fields to display errors.
     * @param template
     * @param errorState
     */
    public enableHiddenFields(template: any, errorState: any): void {
        for (const error of errorState) {
            if (template[error.field]) {
                template[error.field].hidden = false;
            }
        }
    }

    /**
     * Set properties to ignore on submission that are null, or undefined.
     * @param property
     */
    public setPropertiesToIgnoreNullOrUndefined(property: string): void {
        const controllerState = this.wizardService.controllerState;
        this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum.REFERENCE);
        this.utilsService.createArrayIfNotExist(controllerState[WizardTitleEnum.REFERENCE], WizardTitleEnum.PROPERTIES);
        controllerState[WizardTitleEnum.REFERENCE][WizardTitleEnum.PROPERTIES].push(property);
    }

    /**
     * Remove unneeded properties from frontendForm so outputs match l7.virtual.service.model.ts.
     * @param serviceObject
     * @param propertiesToDelete
     */
    public removeUnneededProperties(serviceObject: any, propertiesToDelete: string[]): void {
        for (const property of propertiesToDelete) {
            delete serviceObject[property];
        }
    }
}
